import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
    UploadRowState, 
    OutOfPeriodAdjustmentRow, 
    ValidateRowRequest, 
    ValidateRowResponse,
    UploadRowRequest,
    UploadRowResponse
} from './OutOfPeriodAdjustment';
import { AccountService } from '../../../../utils/ServiceLocator';
import { getToken } from '../../../../utils/AdalConfig';

export type OpaUploadPhase = 'idle' | 'parsing' | 'validating' | 'review' | 'uploading' | 'complete';

interface OpaUploadState {
    rows: UploadRowState[];
    currentIndex: number;
    phase: OpaUploadPhase;
    centerId: string | null;
}

const initialState: OpaUploadState = {
    rows: [],
    currentIndex: -1,
    phase: 'idle',
    centerId: null
};

// Async thunk to validate a single row
export const validateSingleRow = createAsyncThunk<
    ValidateRowResponse,
    ValidateRowRequest,
    { rejectValue: { lineNumber: number; errorMessage: string } }
>(
    'opaUpload/validateSingleRow',
    async (request, { rejectWithValue }) => {
        try {
            const token = getToken();
            const response = await fetch(
                `${AccountService}UploadAdjustmentsEndpoint/ValidateRow/${request.centerId}`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        accountNumber: request.row.accountNumber,
                        period: request.row.period,
                        year: request.row.year,
                        amount: request.row.amount,
                        reason: request.row.reason,
                        lineNumber: request.lineNumber
                    })
                }
            );

            if (!response.ok) {
                const errorText = await response.text();
                return rejectWithValue({ 
                    lineNumber: request.lineNumber, 
                    errorMessage: errorText || 'Validation request failed' 
                });
            }

            const result: ValidateRowResponse = await response.json();
            return result;
        } catch (error) {
            return rejectWithValue({ 
                lineNumber: request.lineNumber, 
                errorMessage: error instanceof Error ? error.message : 'Network error during validation' 
            });
        }
    }
);

// Async thunk to upload a single row
export const uploadSingleRow = createAsyncThunk<
    UploadRowResponse,
    UploadRowRequest,
    { rejectValue: { lineNumber: number; errorMessage: string } }
>(
    'opaUpload/uploadSingleRow',
    async (request, { rejectWithValue }) => {
        try {
            const token = getToken();
            const response = await fetch(
                `${AccountService}UploadAdjustmentsEndpoint/UploadRow/${request.centerId}`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        accountNumber: request.row.accountNumber,
                        period: request.row.period,
                        year: request.row.year,
                        amount: request.row.amount,
                        reason: request.row.reason,
                        lineNumber: request.lineNumber
                    })
                }
            );

            if (!response.ok) {
                const errorText = await response.text();
                return rejectWithValue({ 
                    lineNumber: request.lineNumber, 
                    errorMessage: errorText || 'Upload request failed' 
                });
            }

            const result: UploadRowResponse = await response.json();
            return result;
        } catch (error) {
            return rejectWithValue({ 
                lineNumber: request.lineNumber, 
                errorMessage: error instanceof Error ? error.message : 'Network error during upload' 
            });
        }
    }
);

export const OpaUploadSlice = createSlice({
    name: 'opaUpload',
    initialState,
    reducers: {
        loadRows: (state, action: PayloadAction<{ centerId: string; rows: OutOfPeriodAdjustmentRow[] }>) => {
            state.centerId = action.payload.centerId;
            state.rows = action.payload.rows.map((row, index) => ({
                lineNumber: index + 2, // +2 because line 1 is header, data starts at line 2
                data: row,
                status: 'pending',
                errorMessage: undefined
            }));
            state.phase = 'parsing';
            state.currentIndex = -1;
        },
        setPhase: (state, action: PayloadAction<OpaUploadPhase>) => {
            state.phase = action.payload;
        },
        setCurrentIndex: (state, action: PayloadAction<number>) => {
            state.currentIndex = action.payload;
        },
        setRowValidating: (state, action: PayloadAction<number>) => {
            const row = state.rows.find(r => r.lineNumber === action.payload);
            if (row) {
                row.status = 'validating';
            }
        },
        setRowUploading: (state, action: PayloadAction<number>) => {
            const row = state.rows.find(r => r.lineNumber === action.payload);
            if (row) {
                row.status = 'uploading';
            }
        },
        resetUpload: () => initialState
    },
    extraReducers: (builder) => {
        builder
            // Validate single row handlers
            .addCase(validateSingleRow.pending, (state, action) => {
                const lineNumber = action.meta.arg.lineNumber;
                const row = state.rows.find(r => r.lineNumber === lineNumber);
                if (row) {
                    row.status = 'validating';
                }
            })
            .addCase(validateSingleRow.fulfilled, (state, action) => {
                const row = state.rows.find(r => r.lineNumber === action.payload.lineNumber);
                if (row) {
                    if (action.payload.isValid) {
                        row.status = action.payload.hasWarning ? 'valid-warning' : 'valid';
                        row.warningMessage = action.payload.warningMessage;
                    } else {
                        row.status = 'invalid';
                        row.errorMessage = action.payload.errorMessage;
                    }
                }
            })
            .addCase(validateSingleRow.rejected, (state, action) => {
                if (action.payload) {
                    const row = state.rows.find(r => r.lineNumber === action.payload!.lineNumber);
                    if (row) {
                        row.status = 'invalid';
                        row.errorMessage = action.payload.errorMessage;
                    }
                }
            })
            // Upload single row handlers
            .addCase(uploadSingleRow.pending, (state, action) => {
                const lineNumber = action.meta.arg.lineNumber;
                const row = state.rows.find(r => r.lineNumber === lineNumber);
                if (row) {
                    row.status = 'uploading';
                }
            })
            .addCase(uploadSingleRow.fulfilled, (state, action) => {
                const row = state.rows.find(r => r.lineNumber === action.payload.lineNumber);
                if (row) {
                    row.status = action.payload.success ? 'uploaded' : 'failed';
                    row.errorMessage = action.payload.errorMessage;
                    // Clear warning message on successful upload
                    if (action.payload.success) {
                        row.warningMessage = undefined;
                    }
                }
            })
            .addCase(uploadSingleRow.rejected, (state, action) => {
                if (action.payload) {
                    const row = state.rows.find(r => r.lineNumber === action.payload!.lineNumber);
                    if (row) {
                        row.status = 'failed';
                        row.errorMessage = action.payload.errorMessage;
                    }
                }
            });
    }
});

export const { 
    loadRows, 
    setPhase, 
    setCurrentIndex, 
    setRowValidating, 
    setRowUploading, 
    resetUpload 
} = OpaUploadSlice.actions;

export default OpaUploadSlice.reducer;
